import { Stack } from "@mui/system";
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useShopContext } from "../../../context/shop/shop.context";
import { ShippingMethodIdEnum as ShippingMethodIdEnum } from "../../../context/shop/shop.types";
import { useTranslation } from "react-i18next";
import { useShippingForm } from "./useShippingForm";

interface ShippingStepProps {
  handleNext: () => void;
}

const ShippingStep = ({
  shippingForm,
}: {
  shippingForm: ReturnType<typeof useShippingForm>;
}) => {
  const { t } = useTranslation();
  const { shopState, shopDispatch, shippingMethods } = useShopContext();
  const {
    register,
    watch,
    formState: { errors, isValid, isSubmitted },
  } = shippingForm;

  return (
    <Stack alignItems="flex-start">
      {!isValid && isSubmitted && (
        <Typography color="error" mb={2}>
          {t("page_checkout.shipping.error_missing")}
        </Typography>
      )}
      <Card sx={{ display: "flex", flexDirection: "column", p: 2, mb: 2 }}>
        <FormControl>
          <FormLabel>
            {t("page_checkout.shipping.shippingMethods.title")}
          </FormLabel>
          {shippingMethods?.length > 0 ? (
            <RadioGroup defaultValue={shopState.shippingMethodId}>
              {shippingMethods.map((method) => (
                <FormControlLabel
                  key={method.id}
                  value={method.id}
                  control={
                    <Radio {...register("shippingMethodId")}>
                      {t(`shipping_methods.${method.id}`)}
                    </Radio>
                  }
                  label={t(`shipping_methods.${method.id}`)}
                />
              ))}
            </RadioGroup>
          ) : (
            <CircularProgress size={20} sx={{ mt: 1, alignSelf: "center" }} />
          )}
        </FormControl>
        {watch("shippingMethodId") === ShippingMethodIdEnum.LIVRAISON && (
          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={shopState.isDifferentAddress}
                {...register("isDifferentAddress")}
              />
            }
            label={t("page_checkout.shipping.same-address")}
          />
        )}
      </Card>
      <Stack flexDirection="row">
        <Stack>
          <Typography variant="h6">
            {t("page_checkout.shipping.billing-title")}
          </Typography>
          <TextField
            {...register("billing_first_name", { required: true })}
            required
            error={!!errors.billing_first_name}
            label={t("page_checkout.shipping.first_name")}
          />
          <TextField
            {...register("billing_last_name", { required: true })}
            required
            error={!!errors.billing_last_name}
            label={t("page_checkout.shipping.last_name")}
          />
          <TextField
            {...register("billing_company")}
            label={t("page_checkout.shipping.company")}
          />
          <TextField
            {...register("billing_address_1", { required: true })}
            required
            error={!!errors.billing_address_1}
            label={t("page_checkout.shipping.address_1")}
          />
          <TextField
            {...register("billing_address_2")}
            label={t("page_checkout.shipping.address_2")}
          />
          <TextField
            {...register("billing_city", { required: true })}
            required
            error={!!errors.billing_city}
            label={t("page_checkout.shipping.city")}
          />
          <TextField
            {...register("billing_postcode", { required: true })}
            required
            error={!!errors.billing_postcode}
            label={t("page_checkout.shipping.postcode")}
          />
          <TextField
            {...register("billing_email", {
              required: true,
              pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
            })}
            required
            error={!!errors.billing_email}
            label={t("page_checkout.shipping.email")}
          />
          <TextField
            {...register("billing_phone", {
              required: true,
              pattern:
                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
            })}
            required
            error={!!errors.billing_phone}
            label={t("page_checkout.shipping.phone")}
          />
          {/* <TextField {...register("billing_state")} label={t("page_checkout.shipping.state")} />
        <TextField {...register("billing_country")} label={t("page_checkout.shipping.country")} /> */}
        </Stack>
        {watch("shippingMethodId") === ShippingMethodIdEnum.LIVRAISON &&
          watch("isDifferentAddress") && (
            <Stack sx={{ ml: 2 }}>
              <Typography variant="h6">
                {t("page_checkout.shipping.shipping-title")}
              </Typography>
              <TextField
                {...register("shipping_first_name", { required: true })}
                required
                error={!!errors.shipping_first_name}
                label={t("page_checkout.shipping.first_name")}
              />
              <TextField
                {...register("shipping_last_name", { required: true })}
                required
                error={!!errors.shipping_last_name}
                label={t("page_checkout.shipping.last_name")}
              />
              <TextField
                {...register("shipping_company")}
                label={t("page_checkout.shipping.company")}
              />
              <TextField
                {...register("shipping_address_1", { required: true })}
                required
                error={!!errors.shipping_address_1}
                label={t("page_checkout.shipping.address_1")}
              />
              <TextField
                {...register("shipping_address_2")}
                label={t("page_checkout.shipping.address_2")}
              />
              <TextField
                {...register("shipping_city", { required: true })}
                required
                error={!!errors.shipping_city}
                label={t("page_checkout.shipping.city")}
              />
              <TextField
                {...register("shipping_postcode", { required: true })}
                required
                error={!!errors.shipping_postcode}
                label={t("page_checkout.shipping.postcode")}
              />
              {/* <TextField {...register("shipping_state")} label={t("page_checkout.shipping.state")} />
        <TextField {...register("shipping_country")} label={t("page_checkout.shipping.country")} /> */}
            </Stack>
          )}
      </Stack>
    </Stack>
  );
};
export default ShippingStep;
